import { useState, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import { Button } from '../components/ui/button';
import { UploadSimple, DownloadSimple, FileText, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

export default function ImportExportPage() {
  const { statuses: statusOptions } = useStatusOptions();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [exportCompany, setExportCompany] = useState('');
  const [exportTag, setExportTag] = useState('');
  const fileRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/contacts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = {};
      if (exportStatus) params.status = exportStatus;
      if (exportCompany) params.company = exportCompany;
      if (exportTag) params.tag = exportTag;
      const endpoint = format === 'xlsx' ? '/contacts/export/xlsx' : '/contacts/export/csv';
      const { data } = await api.get(endpoint, {
        params,
        responseType: 'blob',
      });
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const filename = `contacts_export_${new Date().toISOString().slice(0, 10)}.${ext}`;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      let msg = `${filename} saved to your Downloads folder`;
      if (format === 'csv') {
        const text = await data.text();
        let nl = 0, inQ = false;
        for (let i = 0; i < text.length; i++) {
          if (text[i] === '"') inQ = !inQ;
          else if (text[i] === '\n' && !inQ) nl++;
        }
        const records = text.endsWith('\n') ? nl : nl + 1;
        msg = `Exported ${Math.max(0, records - 1)} contacts — ${msg}`;
      } else {
        msg = `Excel export complete — ${msg}`;
      }
      toast.success(msg, { duration: 8000 });
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Export failed — please try again or log out and back in');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="import-export-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#111827]" data-testid="import-export-title">
          Import / Export
        </h1>
        <WatchHowLink slug="smart-import" label="Watch: Smart Import" />
        <WatchHowLink slug="export" label="Watch: Export" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Import */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="import-section">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h2 className="text-base font-semibold text-[#111827] flex items-center gap-2">
              <UploadSimple size={18} weight="bold" className="text-[#002FA7]" />
              Import Contacts
            </h2>
          </div>
          <div className="p-5">
            <p className="text-sm text-[#4B5563] mb-4">
              Upload a CSV file. Columns are auto-mapped by header names (first_name, last_name, company, email, phone, etc.). Unmapped columns become custom fields.
            </p>
            <div
              className="border-2 border-dashed border-[#E5E7EB] rounded-sm p-8 text-center cursor-pointer hover:border-[#002FA7] transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="import-dropzone"
            >
              <FileText size={32} className="mx-auto mb-2 text-[#4B5563]" />
              <p className="text-sm font-medium text-[#111827]">
                {importing ? 'Importing...' : 'Click to select CSV file'}
              </p>
              <p className="text-xs text-[#4B5563] mt-1">Supports .csv files</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
              data-testid="import-file-input"
            />
            {importResult && (
              <div className="mt-4 p-3 rounded-sm border" data-testid="import-result">
                {importResult.error ? (
                  <div className="flex items-center gap-2 text-[#DC2626]">
                    <WarningCircle size={18} weight="bold" />
                    <span className="text-sm">{importResult.error}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[#16A34A]">
                      <CheckCircle size={18} weight="bold" />
                      <span className="text-sm font-medium">{importResult.imported} contacts imported</span>
                    </div>
                    {importResult.errors > 0 && (
                      <p className="text-sm text-[#DC2626] ml-6">{importResult.errors} rows had errors</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Export */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="export-section">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h2 className="text-base font-semibold text-[#111827] flex items-center gap-2">
              <DownloadSimple size={18} weight="bold" className="text-[#002FA7]" />
              Export Contacts
            </h2>
          </div>
          <div className="p-5">
            <p className="text-sm text-[#4B5563] mb-4">
              Download your contacts as a CSV file. Optionally filter by status or company.
            </p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-medium text-[#4B5563] uppercase tracking-wide">Filter by Status</label>
                <Select value={exportStatus} onValueChange={(v) => setExportStatus(v === 'all' ? '' : v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="export-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={() => handleExport('csv')}
                disabled={exporting}
                variant="outline"
                className="w-full rounded-sm gap-2 border-[#B0B0B0]"
                data-testid="export-btn"
              >
                <DownloadSimple size={16} weight="bold" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
              <Button
                onClick={() => handleExport('xlsx')}
                disabled={exporting}
                className="w-full bg-[#1E8449] hover:bg-[#196F3D] text-white rounded-sm gap-2"
                data-testid="export-xlsx-btn"
              >
                <DownloadSimple size={16} weight="bold" />
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </div>
            <p className="text-[11px] text-[#6B7280] mt-2">
              Excel (.xlsx) includes formatted headers, frozen top row, and all custom fields. CSV is plain text for maximum compatibility.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
